import { createTheme } from '@mui/material/styles';

export const themeLayout = {
  footerHeight: '80px',
  headerHeight: '64px',
  sideNavWidth: 296,
  sideNavHiddenWidth: 24,
  minContainerWidthForListView: 921,
  infoCardWidth: '340px',
  mobileWidth: 500,
  hamburgerNavigationWidth: 247,
  breakpointSmall: 600,
  breakpointBig: 780,
  breakpointLarge: 1155,
};

export const themeColors = {
  mbsBlue: '#004a96',
  mbsOrange: '#f39500',
  accentDark: '#2e3237',
  accentLight: '#4f5d6a',
  systemRed: '#d74739',
  systemGreen: '#46b361',
  systemBlue: '#3e94ec',
  greyscale1: '#585b5f',
  greyscale2: '#828487',
  greyscale3: '#c0c2c3',
  greyscale4: '#eaebeb',
  greyscale5: '#f9fafb',
  white: '#ffffff',
  white16: 'rgba(255, 255, 255, 0.16)',
  white50: 'rgba(255,255,255,0.5)',
  highlightBlue: '#f0f6fd',
  highlightRed: '#FFC1C3',
  greyscale4_50: 'rgba(234, 235, 235, 0.5)',
  mobileBackground: '#f2f4f6',
  systemWarning: '#FBF1E9',

  //this color is not included in the theme yet
  greyscale: '#DADADA',
  primaryNavy: '#27465B',
  greyscale6: '#585B5F',
  accentDark60: 'rgba(46, 50, 55, 0.6)',
  accentDark14: 'rgba(46, 50, 55, 0.14)',
  systemGreen20: 'rgba(70, 179, 97, 0.2)',
};

export const themeShadows = {
  //shadows
  shadowMedium: '0px 5px 15px rgba(0, 0, 0, 0.1)',
  shadowLarge: '0px 5px 20px rgba(0, 0, 0, 0.2)',
  shadowSmall: '0px 5px 10px rgba(0, 0, 0, 0.05)',
};

const latoFontFamily = '"Lato", sans-serif';

const latoFont = { fontFamily: latoFontFamily };

export const theme = createTheme({
  // Keep the Material-UI v4 defaults the UI was designed against.
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  },
  palette: {
    primary: {
      light: '#7986cb',
      main: '#3f51b5',
      dark: '#303f9f',
    },
    secondary: {
      main: themeColors.mbsBlue,
    },
  },
  typography: {
    fontFamily: latoFontFamily,
  },
  components: {
    // v4's ButtonBase was content-box; v9's is border-box. Restore it where v4
    // didn't set border-box itself, so app sizes (e.g. `height: 16` on a
    // switchBase) still add the padding like they did in v4.
    MuiIconButton: { styleOverrides: { root: { boxSizing: 'content-box' } } },
    // v4 set these inline on every Modal (incl. Popover/Menu), so classes like
    // `position: 'relative'` on a modal could never move it out of the overlay.
    MuiModal: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiModal-root': {
            position: 'fixed',
            zIndex: theme.zIndex.modal,
            inset: 0,
          },
        }),
      },
    },
    // v4 defaulted Checkbox/Radio/Switch to the secondary color.
    MuiCheckbox: {
      defaultProps: { color: 'secondary' },
      styleOverrides: { root: { boxSizing: 'content-box' } },
    },
    // v4 defaulted inputs to the standard variant (v5+ uses outlined). Selects
    // without their own variant inherit it from the surrounding FormControl.
    MuiFormControl: { defaultProps: { variant: 'standard' } },
    MuiTextField: { defaultProps: { variant: 'standard' } },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { color: 'primary' },
              style: { color: themeColors.white },
            },
            {
              props: { color: 'secondary' },
              style: { color: themeColors.mbsBlue },
            },
          ],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          width: '100%',
          borderRadius: 2,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: themeColors.mbsBlue,
          },
          '&.MuiInputBase-adornedEnd': {
            paddingRight: 4,
          },
        },
        input: {
          padding: '8px 9px',
          fontFamily: latoFontFamily,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&:before': {
            borderColor: 'red',
          },
          '&:after': {
            borderColor: themeColors.greyscale3,
          },
          '&:focus': {
            backgroundColor: themeColors.white,
            borderColor: 'red',
          },
        },
        select: {
          fontFamily: latoFontFamily,
          // v4's min-height; v5+ uses 1.4375em, which top-aligns one-line values.
          '&.MuiSelect-select': {
            minHeight: '1.1876em',
          },
          '&:focus': {
            backgroundColor: themeColors.white,
            borderColor: 'red',
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: { color: 'secondary' },
      styleOverrides: {
        root: {
          boxSizing: 'content-box',
          variants: [
            {
              props: { color: 'secondary' },
              style: {
                '&.Mui-checked': {
                  color: themeColors.mbsBlue,
                },
              },
            },
          ],
        },
      },
    },
    MuiSwitch: {
      defaultProps: { color: 'secondary' },
      styleOverrides: {
        root: {
          width: 70,
        },
        thumb: {
          height: 16,
          width: 16,
        },
        switchBase: {
          boxSizing: 'content-box',
          // Controls default (unchecked) color for the thumb
          color: themeColors.white,
          top: 5,
          left: 4,
          variants: [
            {
              props: { color: 'primary' },
              style: {
                '&.Mui-checked': {
                  // Controls checked color for the thumb
                  transform: 'translateX(27px)',
                  color: themeColors.white,
                },
                // Also beats MUI's .Mui-checked.Mui-disabled thumb color.
                '&.Mui-disabled, &.Mui-checked.Mui-disabled': {
                  // Controls checked color for the track
                  opacity: 1,
                  color: themeColors.white,
                },
              },
            },
          ],
        },
        track: {
          // Controls default (unchecked) color for the track
          opacity: 1,
          width: 50,
          height: 20,
          borderRadius: 10,
          backgroundColor: themeColors.greyscale2,
          '.Mui-checked.Mui-checked + &': {
            // Controls checked color for the track
            opacity: 1,
            backgroundColor: themeColors.mbsBlue,
          },
          '.Mui-checked.Mui-disabled + &': {
            // Controls checked color for the track
            opacity: 1,
            backgroundColor: themeColors.greyscale1,
          },
          '.Mui-disabled.Mui-disabled + &': {
            // Controls checked color for the track
            opacity: 1,
            backgroundColor: themeColors.greyscale3,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          fontFamily: latoFontFamily,
          '&::placeholder': {
            color: 'blue',
            fontSize: '14px',
            lineHeight: '22px',
          },
          fontSize: '14px',
          lineHeight: '22px',
          color: 'white',
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: latoFont } },
    MuiFormLabel: {
      // v4's label line-height; v5+ uses 1.4375em, shifting positioned labels down.
      styleOverrides: { root: { ...latoFont, lineHeight: 1 } },
    },
    MuiButton: { styleOverrides: { root: latoFont } },
    MuiTableCell: { styleOverrides: { root: latoFont } },
    MuiMenuItem: { styleOverrides: { root: latoFont } },
    MuiListItemText: {
      styleOverrides: { primary: latoFont, secondary: latoFont },
    },
    MuiChip: { styleOverrides: { label: latoFont } },
    MuiTab: { styleOverrides: { root: latoFont } },
    MuiDialogTitle: { styleOverrides: { root: latoFont } },
    MuiDialogContentText: { styleOverrides: { root: latoFont } },
    MuiTypography: {
      styleOverrides: {
        body1: {
          fontSize: '14px',
          lineHeight: '18px',
          fontFamily: latoFontFamily,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        },
        body2: {
          fontSize: '16px',
          lineHeight: '20px',
          fontFamily: latoFontFamily,
        },
        subtitle1: {
          fontSize: '18px',
          lineHeight: '22px',
          fontFamily: latoFontFamily,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        },
        subtitle2: {
          fontSize: '14px',
          lineHeight: '18px',
          fontFamily: latoFontFamily,
        },
        h1: {
          fontFamily: latoFontFamily,
          fontSize: '24px',
          lineHeight: '28px',
          fontWeight: 700,
        },
        h2: {
          fontSize: '20px',
          lineHeight: '24px',
          fontFamily: latoFontFamily,
        },
        h3: {
          fontSize: '12px',
          lineHeight: '18px',
          fontFamily: latoFontFamily,
        },
        h4: {
          fontSize: '14px',
          lineHeight: '22px',
          fontFamily: latoFontFamily,
        },
        h5: {
          fontSize: '12px',
          lineHeight: '14px',
          fontFamily: latoFontFamily,
        },
        h6: {
          fontSize: '14px',
          lineHeight: '20px',
          fontFamily: latoFontFamily,
        },
        caption: {
          fontSize: '59px',
          lineHeight: '75px',
          fontFamily: latoFontFamily,
        },
        button: {
          fontSize: '18px',
          lineHeight: '26px',
          textTransform: 'none',
          fontFamily: latoFontFamily,
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        icon: {
          color: '#27465B !important',
        },
      },
    },
  },
});
